import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, CheckCircle2, Circle, Clock, Brain } from "lucide-react";
import { toast } from "sonner";

const CourseView = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  const [lessonAccess, setLessonAccess] = useState({});
  const [courseExpired, setCourseExpired] = useState(false);

  useEffect(() => {
    loadCourseData();
    fetchLessonAccess();
  }, [courseId]);

  const fetchLessonAccess = async () => {
    try {
      const response = await axios.get(`${API}/lessons/access-status`, { withCredentials: true });
      setLessonAccess(response.data.lesson_access || {});
      setCourseExpired(response.data.ai_course_expired || false);
    } catch (error) {
      console.error("Failed to fetch lesson access:", error);
    }
  };

  // Determine course type from courseId
  const getCourseType = () => {
    if (!course) return 'ai';
    const name = course.name?.toLowerCase() || '';
    const subject = course.subject?.toLowerCase() || '';
    if (name.includes('coding') || subject.includes('coding')) return 'coding';
    return 'ai';
  };

  const loadCourseData = async () => {
    try {
      const [courseRes, lessonsRes, progressRes] = await Promise.all([
        axios.get(`${API}/courses/${courseId}`, { withCredentials: true }),
        axios.get(`${API}/courses/${courseId}/lessons`, { withCredentials: true }),
        axios.get(`${API}/progress`, { withCredentials: true })
      ]);

      setCourse(courseRes.data);
      setLessons(lessonsRes.data);
      setProgress(progressRes.data.filter(p => p.course_id === courseId));
    } catch (error) {
      console.error("Failed to load course:", error);
      toast.error("Failed to load course data");
    } finally {
      setLoading(false);
    }
  };

  const getLessonProgress = (lessonId) => {
    const lessonProgress = progress.find(p => p.lesson_id === lessonId);
    return lessonProgress || { completed: false, progress_percentage: 0 };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading course...</p>
        </div>
      </div>
    );
  }

  const completedCount = progress.filter(p => p.completed).length;
  const progressPercent = Math.round((completedCount / lessons.length) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <header className="glass-effect">
        <div className="container mx-auto px-6 py-4">
          <Button
            data-testid="back-to-dashboard-btn"
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="hover:bg-white/50"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      {/* Course Header */}
      <div className="relative h-80 overflow-hidden">
        <img
          src={course?.image_url}
          alt={course?.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-indigo-900 via-indigo-900/70 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="container mx-auto max-w-5xl">
            <div className="flex items-center gap-2 mb-3">
              <Brain className="w-6 h-6 text-white" />
              <span className="text-white font-medium">{course?.weeks} Lesson Program</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">{course?.name}</h1>
            <p className="text-base text-white/90 max-w-2xl">{course?.description}</p>
            
            {/* Progress */}
            <div className="mt-6 max-w-md">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-white/90">Your Progress</span>
                <span className="text-sm font-semibold text-white">{completedCount} / {lessons.length} lessons</span>
              </div>
              <div className="w-full h-3 bg-white/20 rounded-full overflow-hidden backdrop-blur-sm">
                <div
                  className="h-full bg-white transition-all duration-500"
                  style={{ width: `${progressPercent}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lessons */}
      <main className="container mx-auto max-w-5xl px-6 py-12">
        {/* Course Time Limit Notice */}
        <Card className="mb-8 border-3 border-red-400 bg-gradient-to-r from-red-50 to-orange-50 shadow-xl">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-red-400 rounded-full flex items-center justify-center text-3xl">
                  ⏰
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  🎯 Unlock on Mastery - Self-Paced Learning
                </h3>
                {course?.subject === 'AI' ? (
                  <div className="space-y-2">
                    <p className="text-base text-gray-800 font-semibold">
                      🕒 This AI Course will be accessible for <span className="text-green-600 text-xl font-bold">6 MONTHS</span> from your enrollment date.
                    </p>
                    <ul className="text-sm text-gray-700 space-y-1 ml-4">
                      <li>• <strong>Self-paced:</strong> Move as fast or slow as you want!</li>
                      <li>• <strong>Pass the quiz</strong> (80%+) to unlock the next lesson</li>
                      <li>• <strong>Unlimited retries:</strong> Keep trying until you master it</li>
                      <li>• <strong>PDF Workbooks:</strong> Unlock after passing each quiz - yours forever!</li>
                    </ul>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-base text-gray-800 font-semibold">
                      🕒 This Coding Course will be accessible for <span className="text-green-600 text-xl font-bold">6 MONTHS</span> from your enrollment date.
                    </p>
                    <ul className="text-sm text-gray-700 space-y-1 ml-4">
                      <li>• <strong>Self-paced:</strong> Move as fast or slow as you want!</li>
                      <li>• <strong>Pass the quiz</strong> (80%+) to unlock the next lesson</li>
                      <li>• <strong>Unlimited retries:</strong> Keep trying until you master it</li>
                      <li>• <strong>PDF Workbooks:</strong> Unlock after passing each quiz - yours forever!</li>
                    </ul>
                  </div>
                )}
                <p className="text-sm text-green-700 font-bold mt-3 bg-green-100 p-2 rounded">
                  ✨ No rushing! Learn at your own pace and truly master each lesson before moving on.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Free Demo Section - Above Lessons */}
        <Card className="mb-8 border-2 border-yellow-400 bg-gradient-to-r from-yellow-50 to-orange-50 shadow-xl">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              {/* Icon */}
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center text-3xl">
                  🎓
                </div>
              </div>

              {/* Content */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center px-3 py-1 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full uppercase">
                    Free Demo
                  </span>
                  <span className="text-xs text-gray-600">No subscription required</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  🗣️ Try Interactive Chat with Geni - Free!
                </h3>
                <p className="text-sm text-gray-700 mb-4">
                  Experience how our AI teacher works! Chat with Geni to learn about interactive teaching, 
                  course content, student interaction, and downloadable materials. Perfect for trying before subscribing!
                </p>

                <div className="flex gap-3">
                  <Button
                    data-testid="try-free-demo-btn"
                    onClick={() => navigate('/demo-chat')}
                    className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold rounded-full transition-all duration-300 hover:scale-105"
                  >
                    🎤 Try Free Demo Chat
                  </Button>
                  <Button
                    onClick={() => navigate('/checkout')}
                    variant="outline"
                    className="border-2 border-yellow-500 text-yellow-700 hover:bg-yellow-50 rounded-full font-semibold"
                  >
                    💳 Subscribe for Full Access
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <h2 className="text-2xl font-bold text-gray-900 mb-8">
          Course Lessons 
          <span className="text-base font-normal text-gray-600 ml-3">
            🔒 Subscription Required (Lessons 1-10)
          </span>
        </h2>
        <div className="space-y-4">
          {lessons.map((lesson, index) => {
            const lessonProg = getLessonProgress(lesson.id);
            const isCompleted = lessonProg.completed;
            
            // Get access status from backend - use course-specific key
            const courseType = getCourseType();
            const accessKey = `${courseType}_lesson_${lesson.lesson_number}`;
            const accessInfo = lessonAccess[accessKey] || lessonAccess[`lesson_${lesson.lesson_number}`] || {};
            const isAccessible = accessInfo.accessible !== false;
            const lockReason = accessInfo.reason || '';
            const isLocked = !isAccessible || courseExpired;

            return (
              <Card
                key={lesson.id}
                data-testid={`lesson-card-week-${lesson.lesson_number}`}
                className={`border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden ${isLocked ? 'opacity-60' : ''}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {/* Status Icon */}
                    <div className="flex-shrink-0 mt-1">
                      {isLocked ? (
                        <div className="w-6 h-6 text-gray-400">🔒</div>
                      ) : isCompleted ? (
                        <CheckCircle2 className="w-6 h-6 text-green-500" />
                      ) : (
                        <Circle className="w-6 h-6 text-gray-300" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`inline-flex items-center justify-center px-3 py-1 ${isLocked ? 'bg-gray-200 text-gray-500' : 'bg-indigo-100 text-indigo-700'} text-xs font-semibold rounded-full`}>
                          Lesson {lesson.lesson_number}
                        </span>
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Clock className="w-4 h-4" />
                          <span>{lesson.duration_minutes} min</span>
                        </div>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{lesson.title}</h3>
                      <p className="text-sm text-gray-600 mb-4">{lesson.description}</p>

                      {/* Locked Message */}
                      {isLocked && (
                        <div className="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                          <p className="text-sm text-yellow-800 font-medium">
                            {courseExpired ? (
                              <>⏰ Course access expired (40-day limit reached)</>
                            ) : (
                              <>🔒 {lockReason}</>
                            )}
                          </p>
                        </div>
                      )}

                      {/* Progress Bar for In-Progress Lessons */}
                      {!isLocked && !isCompleted && lessonProg.progress_percentage > 0 && (
                        <div className="mb-4">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-xs text-gray-600">In Progress</span>
                            <span className="text-xs font-semibold text-gray-900">{lessonProg.progress_percentage}%</span>
                          </div>
                          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-indigo-600 transition-all duration-500"
                              style={{ width: `${lessonProg.progress_percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      )}

                      <Button
                        data-testid={`start-lesson-btn-week-${lesson.lesson_number}`}
                        onClick={() => {
                          if (isLocked) {
                            if (courseExpired) {
                              toast.error('Course access expired. Please renew your subscription.');
                            } else {
                              toast.error(lockReason || 'This lesson is locked');
                            }
                            return;
                          }
                          navigate(`/lesson/${lesson.id}`);
                        }}
                        disabled={isLocked}
                        className={`${
                          isLocked 
                            ? 'bg-gray-400 cursor-not-allowed'
                            : isCompleted
                            ? 'bg-green-600 hover:bg-green-700'
                            : 'bg-indigo-600 hover:bg-indigo-700'
                        } text-white rounded-full transition-all duration-300`}
                      >
                        {isLocked ? '🔒 Locked' : isCompleted ? 'Review Lesson' : lessonProg.progress_percentage > 0 ? 'Continue' : 'Start Lesson'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default CourseView;
